// Where a dragged column can land, and where every column sits while it drags. A slot is a region
// and a position among that region's other visible columns, so the edge between two regions holds
// two slots: the last pinned column and the first unpinned one. A slot maps onto one
// `moveTableColumnLayout` move. The geometry is the same sum of widths the grid lays out, so the
// preview and the settled table agree to the pixel and a drop doesn't jump.
import type { TableColumnLayoutMove, TableColumnRegion } from '../table/context/table-layout'
import type { TableColumnPinningState } from '../table/tanstack-table'

/** The table as the drag sees it: widths in pixels, the columns that stick, and the viewport. */
export type HeaderDragGeometry = {
  scrollLeft: number
  /**
   * The pinned columns that stick in a layout. On a phone only those within the sticky budget
   * do, and which those are depends on every pinned column, so it's worked out per layout.
   */
  stickyFor: (regions: HeaderDropRegions) => ReadonlySet<string>
  viewportWidth: number
  widths: ReadonlyMap<string, number>
}

/** Columns whose pinning is locked, which keep their end of the table and take no slot past it. */
export type HeaderDropLocks = Pick<TableColumnPinningState, 'end' | 'start'>

/** Each region's visible leaf column ids, in the order the header renders them. */
export type HeaderDropRegions = Record<TableColumnRegion, string[]>

export type HeaderDropSlot = { index: number; region: TableColumnRegion }

type HeaderDropInput = { activeId: string; regions: HeaderDropRegions }

/** How wide an empty pinned region is while dragging, at its side of the table. */
export const HEADER_DROP_EMPTY_BAND = 48

/** The regions in screen order. */
export const HEADER_DROP_REGIONS = ['start', 'center', 'end'] as const

const othersIn = ({ activeId, regions }: HeaderDropInput, region: TableColumnRegion) =>
  regions[region].filter(id => id !== activeId)

/** Every slot the column can land in, in screen order. */
export const getHeaderDropSlots = ({
  locks,
  ...input
}: HeaderDropInput & { locks: HeaderDropLocks }): HeaderDropSlot[] =>
  HEADER_DROP_REGIONS.flatMap(region => {
    const others = othersIn(input, region)
    // Past every locked column at either end, however many there are.
    let first = 0
    while (first < others.length && locks.start.includes(others[first] ?? '')) first++
    let last = others.length
    while (last > first && locks.end.includes(others[last - 1] ?? '')) last--
    return Array.from({ length: Math.max(0, last - first + 1) }, (_, offset) => ({
      index: first + offset,
      region,
    }))
  })

/** The slot the column already sits in. Dropping there changes nothing. */
export const getHeaderHomeSlot = ({ activeId, regions }: HeaderDropInput): HeaderDropSlot => {
  const region =
    HEADER_DROP_REGIONS.find(candidate => regions[candidate].includes(activeId)) ?? 'center'
  return { index: regions[region].indexOf(activeId), region }
}

export const isSameHeaderDropSlot = (first: HeaderDropSlot, second: HeaderDropSlot) =>
  first.region === second.region && first.index === second.index

/** The slot as `moveTableColumnLayout`'s move: before a column, after a region's last, or into an empty region. */
export const getHeaderDropMove = (
  input: HeaderDropInput,
  slot: HeaderDropSlot,
): Pick<TableColumnLayoutMove, 'overId' | 'placement' | 'targetRegion'> => {
  const others = othersIn(input, slot.region)
  const before = others[slot.index]
  if (before !== undefined)
    return { overId: before, placement: 'before', targetRegion: slot.region }
  const last = others.at(-1)
  if (last !== undefined) return { overId: last, placement: 'after', targetRegion: slot.region }
  return { targetRegion: slot.region }
}

/** The regions with the column moved into `slot`, as the drop would leave them. */
export const placeHeaderInSlot = (input: HeaderDropInput, slot: HeaderDropSlot) => {
  const placed = (region: TableColumnRegion) => {
    const others = othersIn(input, region)
    return region === slot.region
      ? [...others.slice(0, slot.index), input.activeId, ...others.slice(slot.index)]
      : others
  }
  return { center: placed('center'), end: placed('end'), start: placed('start') }
}

/**
 * Each column's x from the viewport's left, as the grid lays them out: in order, with sticky
 * start columns held at the left and sticky end columns at the right.
 */
export const getHeaderColumnPositions = (
  regions: HeaderDropRegions,
  { scrollLeft, stickyFor, viewportWidth, widths }: HeaderDragGeometry,
) => {
  const sticky = stickyFor(regions)
  const width = (id: string) => widths.get(id) ?? 0
  const ids = HEADER_DROP_REGIONS.flatMap(region => regions[region])
  const positions = new Map<string, number>()
  let flow = 0
  for (const id of ids) {
    positions.set(id, flow - scrollLeft)
    flow += width(id)
  }
  // A sticky start column sits after the sticky ones before it, wherever the table scrolls.
  let stickyStart = 0
  for (const id of regions.start) {
    if (!sticky.has(id)) continue
    positions.set(id, Math.max(positions.get(id) ?? 0, stickyStart))
    stickyStart += width(id)
  }
  let stickyEnd = 0
  for (const id of [...regions.end].reverse()) {
    if (!sticky.has(id)) continue
    stickyEnd += width(id)
    positions.set(id, Math.min(positions.get(id) ?? 0, viewportWidth - stickyEnd))
  }
  return { endWidth: stickyEnd, positions, startWidth: stickyStart }
}

/** How far each column moves from where it sits to where it sits with the column in `slot`. */
export const getHeaderDragOffsets = (
  input: HeaderDropInput,
  slot: HeaderDropSlot,
  geometry: HeaderDragGeometry,
) => {
  const now = getHeaderColumnPositions(input.regions, geometry).positions
  const preview = getHeaderColumnPositions(placeHeaderInSlot(input, slot), geometry)
  const offsets = new Map<string, number>()
  for (const [id, x] of preview.positions) offsets.set(id, x - (now.get(id) ?? x))
  return {
    endWidth: preview.endWidth,
    offsets,
    positions: preview.positions,
    startWidth: preview.startWidth,
  }
}

/**
 * The slot under the pointer at viewport x `x`, against the table as it shows with the gap open
 * in `slot`. A pinned region is its sticky columns' span (or a band when it has none), so a region
 * holds the pointer once the gap opens in it. Elsewhere the region is `hitRegion`, the region of
 * the head under the pointer, as a pinned column past a phone's sticky budget scrolls with the rest.
 */
export const getHeaderDropSlotAt = ({
  geometry,
  hitRegion,
  locks,
  slot,
  x,
  ...input
}: HeaderDropInput & {
  geometry: HeaderDragGeometry
  hitRegion: TableColumnRegion | undefined
  locks: HeaderDropLocks
  /** The slot the column is over now. */
  slot: HeaderDropSlot
  x: number
}): HeaderDropSlot => {
  const sections = getHeaderDragOffsets(input, slot, geometry)
  const startSpan = sections.startWidth || HEADER_DROP_EMPTY_BAND
  const endSpan = sections.endWidth || HEADER_DROP_EMPTY_BAND
  // The unpinned section keeps a band past the start section even when no other column is
  // unpinned and the end section takes the free width.
  const centerEnd = Math.max(geometry.viewportWidth - endSpan, startSpan + HEADER_DROP_EMPTY_BAND)
  let region: TableColumnRegion = 'end'
  if (x < startSpan) region = 'start'
  else if (x < centerEnd)
    region = hitRegion === 'start' || hitRegion === 'end' ? hitRegion : 'center'
  // Among the region's other columns where they show now, with the gap open: before each
  // column whose middle is past the pointer.
  const before = othersIn(input, region).filter(
    id => (sections.positions.get(id) ?? 0) + (geometry.widths.get(id) ?? 0) / 2 < x,
  ).length
  const allowed = getHeaderDropSlots({ ...input, locks }).filter(each => each.region === region)
  const first = allowed[0]?.index ?? 0
  const last = allowed.at(-1)?.index ?? 0
  return { index: Math.min(Math.max(before, first), last), region }
}

/** The slot one step left or right of `current`, or `undefined` past either end. */
export const stepHeaderDropSlot = (
  slots: HeaderDropSlot[],
  current: HeaderDropSlot,
  step: -1 | 1,
): HeaderDropSlot | undefined =>
  slots[slots.findIndex(slot => isSameHeaderDropSlot(slot, current)) + step]
